import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styles from "./index.module.scss";
import useSetState from "customHooks/setState";
import Repository from "repositories/Repository";
import { useStore, StoreTypes } from "context";
import "./content.scss";
import { parseChineseOnly } from "util/zhuyin";
const { ExtendedResourceRepository } = Repository;

// belowRadical：要顯示在部首標題框（資料 HTML 內的 .wordBox）正下方的元素，例如喇叭（NBK-1743）
// onReady：內容（HTML、圖片、字型）都就緒、可以量測版面時呼叫，供部首分頁使用（NBK-1743）
const WordRadicalContent = ({ htmlPath, imgPath, belowRadical, onReady }) => {
  const [{ bookId, books }] = useStore(StoreTypes.books);
  const [{ html }, setState] = useSetState({ html: "" });
  const rootRef = useRef(null);
  const contentRef = useRef(null);
  const [belowRadicalHost, setBelowRadicalHost] = useState(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  const book=books.find(info=>info.bookId===bookId)
  const elementaryHighGradeCheck =
    book.display_name.includes("國小國語5") ||
    book.display_name.includes("國小國語6");

  useEffect(() => {
    const getHtml = async () => {
      const htmlJson = await ExtendedResourceRepository.getJSONContent({
        bookId,
        pathName: htmlPath ? htmlPath.split(".json")[0] : "",
      });
      setState({ html: htmlJson.html });
    };
    htmlPath && getHtml();
  }, [bookId, htmlPath, setState]);

  // .wordBox 來自資料 HTML（dangerouslySetInnerHTML），無法直接寫 JSX 進去：
  // HTML 渲染後在 .wordBox 內插入掛載點，再以 portal 把 belowRadical 渲染進去
  useEffect(() => {
    if (!html || !contentRef.current) return;
    const wordBox = contentRef.current.querySelector(".wordBox");
    if (!wordBox) return;
    const host = document.createElement("div");
    host.className = styles.belowRadical;
    wordBox.appendChild(host);
    setBelowRadicalHost(host);
    return () => {
      host.remove();
      setBelowRadicalHost(null);
    };
  }, [html]);

  // 內容就緒通知：HTML 已渲染、內容中的圖片載入完成（含失敗）、字型載入完成後才呼叫 onReady，
  // 取代原本固定延遲 1 秒再量測，讓部首能在分頁完成後才顯示（避免畫面跳動）
  useEffect(() => {
    const notify = () => onReadyRef.current && onReadyRef.current();
    if (!htmlPath) {
      notify();
      return;
    }
    if (!html || !rootRef.current) return;
    let cancelled = false;
    const imagesLoaded = Array.from(rootRef.current.querySelectorAll("img")).map(img =>
      img.complete
        ? Promise.resolve()
        : new Promise(resolve => {
          img.addEventListener("load", resolve, { once: true });
          img.addEventListener("error", resolve, { once: true });
        })
    );
    const fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
    Promise.all([...imagesLoaded, fontsReady]).then(() => {
      if (!cancelled) notify();
    });
    return () => {
      cancelled = true;
    };
  }, [html, htmlPath]);

  return (
    <div ref={rootRef} className={styles.wordRadicalContent}>
      {belowRadical && belowRadicalHost && ReactDOM.createPortal(belowRadical, belowRadicalHost)}
      {/* 資料內找不到 .wordBox 時退回顯示在內容最前面，避免喇叭消失 */}
      {belowRadical && html && !belowRadicalHost && (
        <div className={styles.belowRadicalFallback}>{belowRadical}</div>
      )}
      {imgPath && (
        <img
          src={ExtendedResourceRepository.getDataSrc({
            bookId,
            pathName: imgPath,
          })}
          alt=""
        />
      )}
      {html && (
        <div
          ref={contentRef}
          className={styles.content}
          dangerouslySetInnerHTML={{
            __html: elementaryHighGradeCheck ? parseChineseOnly(html,"wordingLearning") : html,
          }}
        ></div>
      )}
    </div>
  );
};

export default WordRadicalContent;
